import { useState, useEffect, useRef, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Zap, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/hooks/useAuth";
import PricingDialog from "@/components/PricingDialog";
import { useLocalCurrency } from "@/hooks/useLocalCurrency";
import { trackConversion } from "@/lib/trackConversion";

interface PremiumPeekProps {
  children: ReactNode;
  sectionId: string;
  duration?: number;
}

const PremiumPeek = ({ children, sectionId, duration = 8 }: PremiumPeekProps) => {
  const { isPremium } = useAuth();
  const { formatPriceWithEur } = useLocalCurrency();
  const storageKey = `peek_seen_${sectionId}`;
  const [phase, setPhase] = useState<"preview" | "locked">(() =>
    sessionStorage.getItem(storageKey) === "1" ? "locked" : "preview"
  );
  const [progress, setProgress] = useState(100);
  const [pricingOpen, setPricingOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (isPremium || phase === "locked") return;

    trackConversion("peek_preview_started", sectionId);
    startRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        setPhase("locked");
        sessionStorage.setItem(storageKey, "1");
        trackConversion("peek_locked", sectionId);
      }
    }, 50);

    return () => clearInterval(timerRef.current);
  }, [isPremium, phase, duration, storageKey, sectionId]);

  if (isPremium) return <>{children}</>;

  return (
    <div className="relative">
      {/* Preview badge + progress bar */}
      <AnimatePresence>
        {phase === "preview" && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-0 left-0 right-0 z-20"
          >
            <Progress value={progress} className="h-1 rounded-none bg-muted" />
            <div className="flex items-center justify-between px-3 py-1.5 bg-primary/10 backdrop-blur-sm">
              <div className="flex items-center gap-1.5">
                <motion.div
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ repeat: Infinity, duration: 1.2 }}
                >
                  <Eye className="h-3.5 w-3.5 text-primary" />
                </motion.div>
                <span className="text-xs font-bold text-primary">LIVE PREVIEW</span>
              </div>
              <span className="text-xs font-mono text-muted-foreground">
                {Math.ceil((progress / 100) * duration)}s remaining
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content with conditional blur */}
      <div
        className="transition-all duration-700"
        style={{
          filter: phase === "locked" ? "blur(8px)" : "none",
          pointerEvents: phase === "locked" ? "none" : "auto",
        }}
      >
        {children}
      </div>

      {/* Locked overlay */}
      <AnimatePresence>
        {phase === "locked" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-sm rounded-lg"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              className="mx-3 sm:mx-4 max-w-sm rounded-xl border border-primary/30 bg-card p-4 sm:p-6 text-center shadow-2xl shadow-primary/10"
            >
              <div className="mx-auto mb-2 sm:mb-3 flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-full bg-primary/15">
                <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              </div>
              <h3 className="text-base sm:text-lg font-black text-foreground">Preview ended</h3>
              <p className="mt-1 text-xs sm:text-sm text-muted-foreground">
                You just saw real intelligence data. Imagine having this 24/7.
              </p>
              <Button
                className="mt-3 sm:mt-4 w-full gap-2 font-bold text-sm sm:text-base"
                onClick={() => { trackConversion("peek_unlock_clicked", sectionId); setPricingOpen(true); }}
              >
                <Zap className="h-4 w-4" />
                Unlock full access
              </Button>
              <p className="mt-2 text-[10px] text-muted-foreground">
                Starting at {formatPriceWithEur(2.99)}/mo — less than a coffee
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
    </div>
  );
};

export default PremiumPeek;
